// src/pages/public/AboutUs.jsx
import React from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { 
  TrendingUp, 
  Shield, 
  Users, 
  Globe, 
  Award, 
  Zap,
  BarChart3,
  Wallet,
  ArrowRight,
  Check,
  Linkedin,
  Star,
  Crown,
  Rocket,
  Building,
  Target,
  DollarSign,
  Clock
} from 'lucide-react'

const AboutUs = () => {
  const stats = [
    { value: '12,320+', label: 'Active Investors', icon: Users },
    { value: '$1.85M+', label: 'Total Invested', icon: TrendingUp },
    { value: '$1.05M+', label: 'Total Withdrawn', icon: Wallet },
    { value: '99.9%', label: 'Platform Uptime', icon: Shield }
  ]

  const values = [
    {
      icon: Shield,
      title: "Institutional Security",
      description: "Military-grade encryption and comprehensive insurance protection for all investments",
      color: "from-[#9E1C60] to-[#811844]"
    },
    {
      icon: Zap,
      title: "Cutting-Edge Innovation",
      description: "AI-powered strategies and real-time execution for optimal investment performance",
      color: "from-[#F5AD18] to-[#9E1C60]"
    },
    {
      icon: TrendingUp,
      title: "Premium Performance",
      description: "Industry-leading ROI with daily compounding growth and proven strategies",
      color: "from-[#811844] to-[#561530]"
    },
    {
      icon: Globe,
      title: "Global Excellence",
      description: "Internationally diversified portfolios managed by world-class experts",
      color: "from-[#561530] to-[#9E1C60]"
    }
  ]

const leadership = [
    {
      name: "James Wilson",
      role: "CEO & Founder",
      bio: "15+ years revolutionizing fintech and blockchain with visionary leadership",
      image: "/team/james.jpg",
      social: { linkedin: "#" },
      achievements: ["Fintech Pioneer", "Blockchain Expert", "Serial Entrepreneur"]
    },
    {
      name: "Priya Patel",
      role: "Chief Technology Officer",
      bio: "Former lead architect at major crypto exchange, driving technical excellence",
      image: "/team/priya.jpg",
      social: { linkedin: "#" },
      achievements: ["Tech Innovator", "Security Specialist", "AI/ML Expert"]
    },
    {
      name: "Marcus Johnson",
      role: "Head of Investment Strategy",
      bio: "Wall Street veteran transforming traditional investment wisdom",
      image: "/team/marcus.jpg",
      social: { linkedin: "#" },
      achievements: ["Portfolio Manager", "Risk Analyst", "Market Strategist"]
    },
    {
      name: "Isabella Garcia",
      role: "Security Director",
      bio: "Cybersecurity authority ensuring bulletproof protection for all assets",
      image: "/team/isabella.jpg",
      social: { linkedin: "#" },
      achievements: ["Security Expert", "Compliance Officer", "Blockchain Auditor"]
    }
  ]

  const milestones = [
    {
      year: "2020",
      title: "Company Founded",
      description: "TGFuture launched with vision to democratize investing"
    },
    {
      year: "2021",
      title: "Platform Launch",
      description: "First AI-powered investment platform released"
    },
    {
      year: "2022",
      title: "Global Expansion",
      description: "Expanded services to 50+ countries worldwide"
    },
    {
      year: "2023",
      title: "$1M+ Milestone",
      description: "Surpassed $1 million in investor withdrawals"
    },
    {
      year: "2024",
      title: "15K+ Investors",
      description: "Growing community of successful investors"
    }
  ]

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-[#561530] via-[#811844] to-[#9E1C60] text-white">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto"
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              About{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F5AD18] to-[#9E1C60]">
                TGFuture
              </span>
            </h1>
            <p className="text-xl text-[#F5AD18] mb-8 leading-relaxed">
              Revolutionizing wealth creation through AI-powered technology, institutional-grade security, 
              and transparent investment solutions for everyone.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="https://workplace.tgfuture.com/register"
                className="bg-[#F5AD18] hover:bg-[#F5AD18]/90 text-[#561530] px-8 py-4 rounded-lg font-semibold text-lg inline-flex items-center gap-3 transition-all transform hover:scale-105 shadow-lg"
              >
                <TrendingUp className="w-6 h-6" />
                Start Investing
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/contact"
                className="border-2 border-white text-white hover:bg-white hover:text-[#561530] px-8 py-4 rounded-lg font-semibold text-lg inline-flex items-center gap-3 transition-all"
              >
                <Users className="w-6 h-6" />
                Meet Our Team
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Banner */}
      <section className="py-16 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="text-center group"
              >
                <div className="bg-gradient-to-r from-[#561530] to-[#9E1C60] w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <stat.icon className="w-8 h-8 text-white" />
                </div>
                <div className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                  {stat.value}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Story */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 bg-[#561530]/10 text-[#561530] dark:bg-[#9E1C60]/20 dark:text-[#F5AD18] px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <Target className="w-4 h-4" />
                Our Mission & Vision
              </div>
              <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">
                Building the Future of{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#9E1C60] to-[#F5AD18]">
                  Intelligent Investing
                </span>
              </h2>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4 p-6 bg-gray-50 dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700">
                  <div className="bg-gradient-to-r from-[#F5AD18] to-[#9E1C60] w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                      Democratizing Investments
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                      Making premium investment opportunities accessible to everyone, 
                      regardless of financial background or experience level.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-6 bg-gray-50 dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700">
                  <div className="bg-gradient-to-r from-[#9E1C60] to-[#811844] w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <Globe className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                      Global Financial Inclusion
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                      Creating borderless investment platforms that empower individuals 
                      worldwide to achieve financial independence.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-6 bg-gray-50 dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700">
                  <div className="bg-gradient-to-r from-[#811844] to-[#561530] w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <BarChart3 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                      AI-Powered Growth
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                      Leveraging artificial intelligence to optimize returns and provide 
                      smart investment strategies for maximum performance.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="bg-gradient-to-br from-[#561530] to-[#811844] rounded-3xl p-8 text-white">
                <div className="max-w-md">
                  <Building className="w-12 h-12 mb-6 text-[#F5AD18]" />
                  <h3 className="text-2xl font-bold mb-4">Our Journey</h3>
                  <p className="text-white/80 leading-relaxed mb-6">
                    Founded in 2020, TGFuture has been at the forefront of investment 
                    innovation, combining traditional financial wisdom with cutting-edge 
                    technology to deliver exceptional results.
                  </p>
                  
                  <div className="space-y-4">
                    {milestones.slice(0, 3).map((milestone, index) => (
                      <div key={milestone.year} className="flex items-center gap-4">
                        <div className="bg-[#F5AD18] text-[#561530] px-3 py-1 rounded-full text-sm font-bold">
                          {milestone.year}
                        </div>
                        <div>
                          <div className="font-semibold">{milestone.title}</div>
                          <div className="text-white/60 text-sm">{milestone.description}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gray-50 dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Our Core Values
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              The principles that guide everything we do at TGFuture
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center group"
              >
                <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-500 border border-gray-200 dark:border-gray-700 group-hover:scale-105 h-full flex flex-col">
                  <div className={`bg-gradient-to-r ${value.color} w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                    <value.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                    {value.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 leading-relaxed flex-grow">
                    {value.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Meet Our Leadership Team
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Experienced professionals dedicated to your investment success
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {leadership.map((member, index) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center group"
              >
                <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-500 border border-gray-200 dark:border-gray-700 group-hover:scale-105 h-full">
                  <div className="bg-gradient-to-r from-[#561530] to-[#9E1C60] w-20 h-20 rounded-2xl mx-auto mb-4 flex items-center justify-center text-white text-xl font-bold shadow-lg">
                    {member.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                    {member.name}
                  </h3>
                  <div className="text-[#9E1C60] dark:text-[#F5AD18] font-semibold mb-3 text-sm">
                    {member.role}
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 leading-relaxed">
                    {member.bio}
                  </p>
                  
                  <div className="flex flex-wrap gap-1.5 justify-center mb-4">
                    {member.achievements.map((achievement, idx) => (
                      <span 
                        key={idx}
                        className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs px-2 py-1 rounded-full font-medium"
                      >
                        {achievement}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      {/* <section className="py-20 bg-gray-50 dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Our Journey
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Milestones in our mission to revolutionize investing
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-5 gap-8">
              {milestones.map((milestone, index) => (
                <motion.div
                  key={milestone.year}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center group"
                >
                  <div className="bg-gradient-to-r from-[#561530] to-[#9E1C60] w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <span className="text-white font-bold text-sm">{milestone.year}</span>
                  </div>
                  <h3 className="font-bold text-gray-900 dark:text-white mb-2">
                    {milestone.title}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                    {milestone.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section> */}

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#561530] to-[#811844] text-white">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-4xl font-bold mb-6">
              Ready to Build Your Future With Us?
            </h2>
            <p className="text-xl mb-8 text-[#F5AD18]">
              Join our growing community of successful investors and start your journey to financial freedom
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="https://workplace.tgfuture.com/register"
                className="bg-[#F5AD18] hover:bg-[#F5AD18]/90 text-[#561530] px-8 py-4 rounded-lg font-semibold text-lg inline-flex items-center gap-3 transition-all transform hover:scale-105 shadow-lg"
              >
                <Rocket className="w-6 h-6" />
                Start Investing
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/contact"
                className="border-2 border-white text-white hover:bg-white hover:text-[#561530] px-8 py-4 rounded-lg font-semibold text-lg inline-flex items-center gap-3 transition-all"
              >
                <Users className="w-6 h-6" />
                Contact Team
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default AboutUs